/**
 * GA4 / GTM dataLayer helpers shared across storefront surfaces.
 *
 * These mirror the legacy {@code touch.js} dataLayer pushers so the existing GTM
 * tags receive the identical {@code event} + {@code ecommerce} shape they already
 * expect — we are reproducing the legacy integration as-is, not inventing a new
 * analytics contract.
 */

/**
 * Pushes one payload to the dataLayer without ever throwing.
 *
 * <p>Almost every helper below runs inside a click handler and pushes BEFORE
 * performing the user's action, so an exception here would cost the customer the
 * click itself — not just the analytics event. Nothing in the container can make
 * {@code push} throw today; this exists so that stays true regardless of what
 * GTM, a consent tool or a browser extension later does to {@code dataLayer}.
 *
 * @param dataLayer the live dataLayer array (created by the caller if absent)
 * @param payload   the event object to enqueue
 */
function safePush(dataLayer: Array<Record<string, unknown>>, payload: Record<string, unknown>): void {
  try {
    dataLayer.push(payload);
  } catch (error) {
    console.warn('GA4 dataLayer push failed:', error);
  }
}

/** Input for {@link dataLayerPushViewItem}. */
export interface ViewItemInput {
  productId: string;
  productName: string;
  /** GA4 {@code item_list_name} (legacy {@code data-section}). */
  listName: string;
  /** 0-based position within the list (legacy {@code data-position}). */
  position: number | string;
  /**
   * Full / reference UNIT price (legacy {@code data-price}). ALWAYS per-unit,
   * never a line total — regardless of {@link quantity}. Used only to derive the
   * per-unit discount ({@code price - promoPrice}); a non-discounted item must
   * pass the same value as {@link promoPrice} so the discount is 0.
   */
  price: number;
  /** Final / promo UNIT price (legacy {@code data-promo-price}). ALWAYS per-unit. */
  promoPrice: number;
  /** GA4 {@code quantity} (legacy {@code data-quantity}). Defaults to 1. Does NOT scale the price. */
  quantity?: number;
  brand?: string;
  category?: string;
  /** Colour description; empty → {@code 'non-variant'} (legacy parity). */
  color?: string;
}

/**
 * Pushes the legacy-compatible GA4 {@code view_item} event for a product-tile
 * click, mirroring {@code dataLayerPushViewItem(cT, title)} in legacy touch.js:
 * the {@code ecommerce} object is reset to {@code null} first (so consecutive
 * events never merge), then a single-item {@code view_item} payload is pushed.
 *
 * <p>Pricing rule (project-wide): {@code price} and {@code promoPrice} are ALWAYS
 * per-unit values, in any case of quantity. The GA4 {@code price} is the final
 * UNIT price ({@code promoPrice} floored to two decimals) and {@code discount} is
 * the per-unit {@code price - promoPrice} (the legacy "extract the discount from
 * these two values" rule). Quantity is reported as-is and never scales the price,
 * so callers that have a line total must divide it back to a unit price first.
 * No-op during SSR (no {@code window}).
 *
 * @param input the product + list context for the clicked tile
 */
export function dataLayerPushViewItem(input: ViewItemInput): void {
  if (typeof window === 'undefined') {
    return;
  }
  const quantity = input.quantity && input.quantity > 0 ? input.quantity : 1;
  const discount = Number((input.price - input.promoPrice).toFixed(2));
  const unitPrice = Number((Math.floor(input.promoPrice * 100) / 100).toFixed(2));

  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, { ecommerce: null });
  safePush(w.dataLayer, {
    event: 'view_item',
    'gtm.element.title': input.listName,
    ecommerce: {
      currency: 'EUR',
      items: [
        {
          price: unitPrice,
          discount,
          index: input.position,
          item_id: input.productId,
          quantity,
          item_name: input.productName,
          item_list_name: input.listName,
          item_brand: input.brand || '',
          item_category: input.category || '',
          item_variant: input.color ? input.color : 'non-variant',
        },
      ],
    },
  });
}

/** Input for {@link dataLayerPushSelectItem}. */
export interface SelectItemInput {
  /**
   * The element carrying the item {@code data-*} attributes (the legacy {@code cT}) — the
   * card's product link built by {@link buildViewItemLink}, which already exposes every
   * field this event needs: {@code data-section} (→ {@code item_list_name}),
   * {@code data-product-id}, {@code data-product-name}, {@code data-product-brand},
   * {@code data-product-category}, {@code data-color}, {@code data-position},
   * {@code data-price} (full/reference UNIT) and {@code data-promo-price} (final UNIT).
   * Reusing the existing link keeps a single inspectable source on the DOM (no duplicate
   * stamping) — the same "read it off the link" pattern used for the cart {@code +} add.
   */
  element: HTMLElement;
  /** GTM {@code gtm.element.title} (legacy {@code title}) — the on-screen section heading, e.g. "Συνδύασέ το με...". */
  title: string;
  /**
   * GTM {@code gtm.element.dataset.action} (legacy {@code action}): {@code "Select"} when the
   * item becomes selected, {@code "Reject"} when it is deselected.
   */
  action: string;
}

/** Legacy default for the {@code sticky} field on the {@code select_item} push. */
const SELECT_ITEM_STICKY = "The function isn't available for this event";

/**
 * Pushes the legacy-compatible GA4 {@code select_item} event, mirroring
 * {@code dataLayerPushSelectItem(cT, title, action)} in legacy touch.js: the
 * {@code ecommerce} object is reset to {@code null} first, then a single-item
 * {@code select_item} payload is pushed with the {@code gtm.element.*} keys (the
 * {@code title}/{@code sticky}/{@code action}) plus the {@code ecommerce} object
 * ({@code currency}/{@code items}). The single item carries {@code quantity: 1}, the
 * floored UNIT {@code price} ({@code data-promo-price}), the per-unit {@code discount}
 * ({@code data-price − data-promo-price}), the {@code item_list_name}
 * ({@code data-section}, legacy {@code cT.dataset.section}) and the {@code item_variant}
 * ({@code 'non-variant'} fallback). Fires on selecting OR deselecting a list item
 * (legacy "Similar Products" Select/Reject toggle). No-op during SSR.
 *
 * @param input the selected element (its product link) + title + Select/Reject action
 */
export function dataLayerPushSelectItem(input: SelectItemInput): void {
  if (typeof window === 'undefined') {
    return;
  }
  const d = input.element.dataset;
  const promoPrice = Number(d.promoPrice) || 0;
  const fullPrice = Number(d.price);
  const unitPrice = Number((Math.floor(promoPrice * 100) / 100).toFixed(2));
  const discount = Number(((Number.isFinite(fullPrice) ? fullPrice : promoPrice) - promoPrice).toFixed(2));

  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, { ecommerce: null });
  safePush(w.dataLayer, {
    event: 'select_item',
    'gtm.element.title': input.title,
    'gtm.element.dataset.sticky': SELECT_ITEM_STICKY,
    'gtm.element.dataset.action': input.action,
    ecommerce: {
      currency: 'EUR',
      items: [
        {
          quantity: 1,
          price: unitPrice,
          discount,
          index: d.position ?? '0',
          item_name: d.productName || '',
          item_list_name: d.section || '',
          item_brand: d.productBrand || '',
          item_category: d.productCategory || '',
          item_id: d.productId || '',
          item_variant: d.color ? d.color : 'non-variant',
        },
      ],
    },
  });
}

/** Input for {@link dataLayerPushAddToCart}. */
export interface AddToCartInput {
  /**
   * The MAIN product element(s) carrying the legacy item {@code data-*} attributes —
   * the legacy {@code productInfoElement}. For a single add pass one element (the
   * add-to-cart button / {@code product-btn-container}); for the multiple-items
   * surface this is still the main product element (the selected items go in
   * {@link AddToCartInput.otherElements}).
   *
   * <p>Each element must expose: {@code data-product-id}, {@code data-product-name},
   * {@code data-product-brand}, {@code data-product-category}, {@code data-color},
   * {@code data-price} (full/reference UNIT), {@code data-promo-price} (final UNIT),
   * {@code data-position}, {@code data-quantity}, and optional {@code data-eservices}.
   * Keeping the values on the DOM makes them inspectable on the button for debugging.
   */
  elements: HTMLElement[];
  /**
   * The OTHER selected items (legacy {@code productInfoElements}) — multiple-items
   * surface only (PDP "Συνδύασέ το με → ΠΡΟΣΘΗΚΗ ΕΠΙΛΕΓΜΕΝΩΝ"). Each carries its own
   * {@code data-*}; its {@code data-position} becomes the item {@code index}. Legacy
   * orders the {@code items} array as {@code [...otherElements, ...elements]}.
   */
  otherElements?: HTMLElement[];
  /**
   * Legacy {@code isMultipleItems}. When true: the main {@code elements} get
   * {@code index '-1'} and e-services are forced to {@code 'not-selected'} on every
   * item (legacy skips the PDP e-services block for multi adds).
   */
  multiple?: boolean;
  /** GTM {@code gtm.element.title} (legacy {@code title}), e.g. "Προσθήκη στο καλάθι - Single Item". */
  title: string;
  /** GTM {@code gtm.element.dataset.sticky} (legacy {@code sticky}). */
  sticky: boolean | string;
  /** GTM {@code gtm.element.dataset.action} (legacy {@code action}). Defaults to the legacy constant. */
  action?: string;
  /**
   * Units added, when it is not 1. Legacy hardcodes {@code quantity: 1} for every add
   * EXCEPT the e-service-only add, where it overrides the default with the attached
   * product line's quantity (touch.js 350): an e-service always follows its product, so
   * attaching insurance to a line of 3 attaches 3. Leave unset everywhere else — a PDP
   * add and a cart "+" each add exactly one unit.
   */
  quantity?: number;
  /**
   * The e-services attached to the product being added, as companion rows (built by
   * {@link eServiceCompanionLines}). They are appended to {@code items} after the
   * product and their price counts towards {@code value} — legacy parity, where the
   * e-service was its own commerce item. Their presence also adds the legacy
   * " με πακέτο/α Insurance" suffix to the title.
   */
  eServiceLines?: CartLineGa4[];
}

/** Legacy default for the {@code action} field on the {@code add_to_cart} push. */
const ADD_TO_CART_DEFAULT_ACTION = "The function isn't available for this event";

/**
 * Legacy {@code data-product-brand} value stamped on bundle add-to-cart elements
 * ("Product Detail Page Bundle"): a bundle has no single manufacturer, so legacy
 * uses this literal. Use it as the {@code data-product-brand} for bundle items.
 */
export const BUNDLE_BRAND_PLACEHOLDER = "Manufacturer isn't available for bundles";

/**
 * Reads ONE GA4 {@code add_to_cart} item from an element's {@code data-*} attributes —
 * the legacy {@code a.dataset.*} extraction lifted into its own helper. Pricing rule
 * (project-wide, identical to {@link dataLayerPushViewItem}): {@code price} is the final
 * UNIT price (promo floored to two decimals) and {@code discount} is the per-unit
 * {@code data-price − data-promo-price}.
 *
 * @param el the element carrying the item {@code data-*} attributes
 * @return the mapped GA4 item and its raw promo UNIT price (for {@code value} summing)
 */
function readAddToCartItem(
  el: HTMLElement,
  opts?: { indexOverride?: string; forceNoEservices?: boolean; quantityOverride?: number },
): { item: Record<string, unknown>; promoPrice: number } {
  const d = el.dataset;
  const promoPrice = Number(d.promoPrice) || 0;
  const fullPrice = Number(d.price);
  const unitPrice = Number((Math.floor(promoPrice * 100) / 100).toFixed(2));
  const discount = Number(((Number.isFinite(fullPrice) ? fullPrice : promoPrice) - promoPrice).toFixed(2));
  return {
    promoPrice,
    item: {
      // Legacy parity: add_to_cart reports ONE unit added (touch.js hardcodes
      // quantity: 1), regardless of the element's data-quantity (which mirrors the
      // cart line quantity and is used by remove_from_cart, not add_to_cart). The one
      // documented exception is the e-service-only add, which overrides it with the
      // attached line's quantity — see AddToCartInput.quantity.
      quantity: opts?.quantityOverride ?? 1,
      price: unitPrice,
      eservices: opts?.forceNoEservices ? 'not-selected' : (d.eservices || 'not-selected'),
      item_id: d.productId || '',
      item_name: d.productName || '',
      item_brand: d.productBrand || '',
      index: opts?.indexOverride ?? d.position ?? '0',
      item_category: d.productCategory || '',
      item_variant: d.color ? d.color : 'non-variant',
      discount,
    },
  };
}

/**
 * Gathers the GA4 {@code add_to_cart} line items + the summed {@code value} from the
 * element list — the legacy {@code productInfoMap} build step lifted into its own
 * sub-function (the same separation legacy touch.js used: map each element's dataset
 * into the {@code productInfoMap}, accumulating {@code totalPrice}, then push).
 * {@code value} sums every item's promo price, including the e-service companion rows
 * ({@link AddToCartInput.eServiceLines}) appended after the product. For multi adds the
 * items are ordered {@code [...otherElements, ...elements]} with the main element(s)
 * forced to {@code index '-1'} and every item's e-services forced to
 * {@code 'not-selected'} (legacy parity).
 *
 * @param input the add-to-cart input (main {@code elements}, optional
 *              {@code otherElements}, and the {@code multiple} flag)
 * @return the mapped GA4 items and their summed {@code value}
 */
function buildAddToCartItems(
  input: AddToCartInput,
): { items: Array<Record<string, unknown>>; value: number } {
  const multiple = Boolean(input.multiple);
  const others = input.otherElements ?? [];
  let value = 0;
  const items: Array<Record<string, unknown>> = [];

  // Legacy order: map the OTHER selected items first (index = their data-position)…
  for (const el of others) {
    const { item, promoPrice } = readAddToCartItem(el, { forceNoEservices: multiple });
    value += promoPrice;
    items.push(item);
  }
  // …then the main product element(s). Legacy hardcodes the main element's index:
  // '-1' for a multi add, '0' for a single add (the element's data-position is only
  // ever read for the OTHER items above).
  for (const el of input.elements) {
    const { item, promoPrice } = readAddToCartItem(el, {
      indexOverride: multiple ? '-1' : '0',
      forceNoEservices: multiple,
      quantityOverride: input.quantity,
    });
    value += promoPrice;
    items.push(item);
  }
  // …and finally the e-services riding along with the product, each a row of its own
  // (legacy's eServiceCommerceItem) whose price counts towards the added value.
  for (const line of input.eServiceLines ?? []) {
    items.push(mapCartLineToGa4Item(line));
    value += line.unitPrice * line.quantity;
  }
  return { items, value: Number(value.toFixed(2)) };
}

/**
 * Pushes the legacy-compatible GA4 {@code add_to_cart} event, mirroring
 * {@code dataLayerPushAddToCart} in legacy touch.js: the {@code ecommerce} object is
 * reset to {@code null} first, the items are gathered from the elements' {@code data-*}
 * via {@link buildAddToCartItems} (the legacy {@code productInfoMap} step), then a single
 * {@code add_to_cart} payload is pushed with the {@code gtm.element.*} keys the GTM "GA4
 * Add To Cart" tag reads ({@code title}/{@code sticky}/{@code action}) plus the
 * {@code ecommerce} object ({@code currency}/{@code items}/{@code value}). No-op during SSR.
 *
 * @param input the element(s) + GTM context for the add-to-cart action
 */
export function dataLayerPushAddToCart(input: AddToCartInput): void {
  if (typeof window === 'undefined') {
    return;
  }
  const { items, value } = buildAddToCartItems(input);
  // Legacy appends this to the title whenever the add carries insurance (touch.js 326).
  const title = (input.eServiceLines?.length ?? 0) > 0
    ? `${input.title} με πακέτο/α Insurance`
    : input.title;

  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, { ecommerce: null });
  safePush(w.dataLayer, {
    event: 'add_to_cart',
    'gtm.element.title': title,
    'gtm.element.dataset.sticky': input.sticky,
    'gtm.element.dataset.action': input.action ?? ADD_TO_CART_DEFAULT_ACTION,
    ecommerce: {
      currency: 'EUR',
      items,
      value,
    },
  });
}

/**
 * ONE GA4 ecommerce cart line, shared by {@link dataLayerPushRemoveFromCart} and
 * {@link dataLayerPushViewCart}. Mirrors the legacy item shape (touch.js): {@code price}
 * is the final UNIT price, {@code discount} is the per-unit {@code reference − unit}, and
 * {@code quantity} is the line quantity (the cart "−" overrides it to {@code 1} for the
 * one-by-one remove). The same pricing-view values that feed {@link buildViewItemLink}
 * on these surfaces are passed here so {@code view_item}/{@code add_to_cart}/
 * {@code remove_from_cart}/{@code view_cart} all report identical product numbers.
 */
export interface CartLineGa4 {
  /** Legacy {@code item_id} (product/sku id). */
  itemId: string;
  /** Legacy {@code item_name}. */
  itemName: string;
  /** Legacy {@code item_brand} (manufacturer). */
  itemBrand?: string;
  /** Legacy {@code item_category}. */
  itemCategory?: string;
  /** Legacy {@code data-color}; absent ⇒ {@code item_variant 'non-variant'}. */
  color?: string;
  /** Legacy {@code index} (list position). */
  index?: number | string;
  /** Final UNIT selling price (legacy {@code unitPrice} = promo floored to two decimals). */
  unitPrice: number;
  /** UNIT reference price for the discount; absent ⇒ no discount ({@code = unitPrice}). */
  referenceUnitPrice?: number;
  /** Quantity removed (button-dependent: 1 for the cart "−", line qty for trash / empty-cart). */
  quantity: number;
  /** Legacy {@code eservices} string; defaults to {@code 'not-selected'}. */
  eservices?: string;
}

/** Input for {@link dataLayerPushRemoveFromCart}. */
export interface RemoveFromCartInput {
  /** Lines removed. One for the cart "−" / mini-cart trash; all cart lines for "Άδειασμα καλαθιού". */
  lines: CartLineGa4[];
  /** GTM {@code gtm.element.title} (legacy {@code title}). */
  title: string;
  /** GTM {@code gtm.element.dataset.sticky}. Defaults to the legacy remove constant. */
  sticky?: boolean | string;
  /** GTM {@code gtm.element.dataset.action}. Defaults to the legacy remove constant. */
  action?: string;
}

/** Legacy default for {@code sticky}/{@code action} on the {@code remove_from_cart} push. */
const REMOVE_FROM_CART_DEFAULT = "The function isn't available for this event";

/**
 * Pushes the legacy-compatible GA4 {@code remove_from_cart} event, mirroring
 * {@code dataLayerPushRemoveFromCart} in legacy touch.js: the {@code ecommerce} object
 * is reset to {@code null} first, then a single {@code remove_from_cart} payload is
 * pushed with the {@code gtm.element.*} keys the GTM "GA4 Remove From Cart" tag reads
 * ({@code title}/{@code sticky}/{@code action}) plus the {@code ecommerce} object
 * ({@code currency}/{@code items}/{@code value}). {@code price} is the floored UNIT
 * price, {@code discount} the per-unit {@code reference − unit}, {@code quantity} the
 * removed amount, and {@code value} the summed {@code unit × quantity} across lines
 * (mirrors the legacy single-item {@code value = dataset.promoPrice} for the line-total
 * case and {@code unit} for the one-by-one "−" case). No-op during SSR.
 *
 * @param input the line(s) + GTM context for the remove-from-cart action
 */
/**
 * Maps ONE {@link CartLineGa4} to the legacy GA4 ecommerce item shape shared by
 * {@code remove_from_cart}, {@code view_cart} and {@code begin_checkout}: floored UNIT
 * {@code price}, per-unit {@code discount} ({@code reference − unit}), the line
 * {@code quantity}, and the {@code item_variant} ({@code 'non-variant'} fallback).
 *
 * @param line the GA4 cart line
 * @return the mapped ecommerce item
 */
function mapCartLineToGa4Item(line: CartLineGa4): Record<string, unknown> {
  const unitPrice = Number((Math.floor(line.unitPrice * 100) / 100).toFixed(2));
  const reference = typeof line.referenceUnitPrice === 'number' ? line.referenceUnitPrice : unitPrice;
  const discount = Number((reference - line.unitPrice).toFixed(2));
  return {
    price: unitPrice,
    eservices: line.eservices || 'not-selected',
    discount,
    index: line.index ?? '0',
    item_id: line.itemId || '',
    item_name: line.itemName || '',
    item_brand: line.itemBrand || '',
    quantity: line.quantity,
    item_category: line.itemCategory || '',
    item_variant: line.color ? line.color : 'non-variant',
  };
}

export function dataLayerPushRemoveFromCart(input: RemoveFromCartInput): void {
  if (typeof window === 'undefined') {
    return;
  }
  const items = input.lines.map(mapCartLineToGa4Item);
  // remove value = Σ unit × quantity (legacy single-item value = the line promo total
  // for the trash / empty-cart case, and the unit price for the one-by-one "−").
  const value = input.lines.reduce(
    (sum, line) => sum + Number((Math.floor(line.unitPrice * 100) / 100).toFixed(2)) * line.quantity,
    0,
  );

  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, { ecommerce: null });
  safePush(w.dataLayer, {
    event: 'remove_from_cart',
    'gtm.element.title': input.title,
    'gtm.element.dataset.sticky': input.sticky ?? REMOVE_FROM_CART_DEFAULT,
    'gtm.element.dataset.action': input.action ?? REMOVE_FROM_CART_DEFAULT,
    ecommerce: {
      currency: 'EUR',
      items,
      value: Number(value.toFixed(2)),
    },
  });
}

/**
 * Legacy {@code item_brand}/{@code item_category} for an e-service line. The legacy
 * cart JSP (newCartEservice.jsp) hardcodes {@code data-product-brand="Insurance"} and
 * {@code data-product-category="Insurance"} on the e-service row — an e-service has no
 * manufacturer or catalog category of its own.
 */
export const ESERVICE_GA4_BRAND = 'Insurance';

/** e-service type id → SAP insurance code, for responses that omit {@code insuranceType}. */
const ESERVICE_TYPE_TO_INSURANCE_ID: Record<string, string> = {
  eServiceUpfront: 'UPFRONT',
  eServiceWExtension: 'WEXTENSION',
};

/**
 * THE identifier for an e-service in GA4: SAP's raw insurance code ({@code UPFRONT} /
 * {@code WEXTENSION}). Used as {@code item_id} on an e-service's own add/remove events
 * AND inside the {@code eservices} string on a product's {@code add_to_cart}, so one
 * value identifies an e-service across every event. Prefers the {@code insuranceType}
 * the backend sent; falls back to mapping the e-service type id
 * ({@code eServiceUpfront} / {@code eServiceWExtension}) when a response omits it.
 *
 * @param insuranceType the backend's raw insurance code, when present
 * @param eServiceId    the e-service type id, used only as a fallback
 * @return the insurance id, or an empty string when neither is known
 */
export function eServiceInsuranceId(insuranceType?: string, eServiceId?: string): string {
  if (insuranceType) {
    return insuranceType;
  }
  if (!eServiceId) {
    return '';
  }
  return ESERVICE_TYPE_TO_INSURANCE_ID[eServiceId] ?? eServiceId;
}

/** One e-service as it hangs off a cart / order line. */
export interface LineEService {
  /** e-service type id (eServiceUpfront / eServiceWExtension). */
  eServiceId?: string;
  /** SAP insurance code, when the backend included it. */
  insuranceType?: string;
  /** Customer-facing name — the companion row's {@code item_name}. */
  displayName?: string;
  /** UNIT price (the backend prices e-services per unit; the line total is price × parent quantity). */
  price?: number;
}

/**
 * The {@code eservices} string for a PRODUCT line: the insurance ids
 * ({@link eServiceInsuranceId}) of every e-service attached to it, comma-joined, or
 * {@code 'not-selected'} when it has none. An e-service follows its product, so the
 * line reports the same set whether it is being added, viewed, removed or purchased.
 *
 * <p>Note this is the "what is attached to me" sense of the field. When the reported
 * item IS an e-service, the field is {@code 'non-variant'} instead — see
 * {@link dataLayerPushRemoveEService}.
 *
 * @param items       the line's e-services
 * @param fallbackIds e-service type ids, for responses carrying only the id list
 * @return the comma-joined insurance ids, or {@code 'not-selected'}
 */
export function lineEservicesString(items?: LineEService[], fallbackIds?: string[]): string {
  const source: LineEService[] = items?.length
    ? items
    : (fallbackIds ?? []).map((id) => ({ eServiceId: id }));
  const ids = source
    .map((es) => eServiceInsuranceId(es.insuranceType, es.eServiceId))
    .filter(Boolean);
  return ids.length > 0 ? ids.join(',') : 'not-selected';
}

/**
 * Builds the COMPANION rows a product line's e-services contribute to an ecommerce
 * event — legacy's {@code eServiceCommerceItem} items, which rode along in every
 * {@code items} array because ATG modelled an e-service as its own commerce item.
 * Our cart nests them under their line instead, so the rows are synthesized here.
 *
 * <p>Each row is the e-service itself: {@code item_id} = the insurance id, brand and
 * category = {@code Insurance}, {@code eservices} = {@code 'non-variant'} (the item IS
 * an e-service), and {@code item_variant} = the parent product's name, which is what
 * ties it back to the product it was combined with — GA4's items array is flat.
 *
 * <p>{@code quantity} is the parent's, because an e-service follows its product: the
 * caller passes whatever quantity the PRODUCT row reports in that same event (1 for an
 * add or a cart "−", the full line quantity for a trash / view_cart / purchase).
 *
 * @param eServices  the line's e-services
 * @param parent     the product row's name and reported quantity
 * @param startIndex the first free {@code index} (rows are numbered from here)
 * @return one cart line per e-service, empty when the line carries none
 */
export function eServiceCompanionLines(
  eServices: LineEService[] | undefined,
  parent: { name: string; quantity: number },
  startIndex = 0,
): CartLineGa4[] {
  return (eServices ?? []).map((es, i) => {
    const insuranceId = eServiceInsuranceId(es.insuranceType, es.eServiceId);
    return {
      itemId: insuranceId,
      itemName: es.displayName || insuranceId,
      itemBrand: ESERVICE_GA4_BRAND,
      itemCategory: ESERVICE_GA4_BRAND,
      color: parent.name,
      index: startIndex + i,
      unitPrice: es.price ?? 0,
      quantity: parent.quantity,
      eservices: 'non-variant',
    };
  });
}

/** Input for {@link dataLayerPushSelectEServices}. */
export interface SelectEServicesInput {
  /** The state the click moves TO: {@code true} when it selects, {@code false} when it unselects. */
  selected: boolean;
  /** The product the e-service is offered on. */
  productName: string;
  /** The product's final UNIT price (legacy {@code data-promo-price}). */
  productPrice: number;
  /** The product's manufacturer. */
  productBrand: string;
  /** The insurance id ({@link eServiceInsuranceId}) of the toggled e-service. */
  serviceType: string;
  /** The e-service's UNIT price. */
  servicePrice: number;
}

/**
 * Pushes the legacy {@code Select_Eservices} event (main.js), fired whenever a customer
 * ticks OR unticks an insurance box on the PDP or the cart — {@code selected} carries
 * which way the click went, so GA4 sees considered-then-dropped services too, not just
 * the ones that survive to an {@code add_to_cart}.
 *
 * <p>Legacy fires this on the click itself, before any cart call, because the event
 * reports INTENT rather than cart contents — unlike the e-service
 * {@code add_to_cart}/{@code remove_from_cart}, which wait for the backend to confirm.
 * Values are pushed as strings, the way legacy read them off the DOM dataset.
 *
 * @param input the toggled service and the product it belongs to
 */
export function dataLayerPushSelectEServices(input: SelectEServicesInput): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Select_Eservices',
    'gtm.element.dataset.selected': input.selected,
    'gtm.element.dataset.productName': input.productName,
    'gtm.element.dataset.productPrice': String(input.productPrice),
    'gtm.element.dataset.productBrand': input.productBrand,
    'gtm.element.dataset.serviceType': input.serviceType,
    'gtm.element.dataset.servicePrice': String(input.servicePrice),
    // Legacy reads this off the e-service element, where it is always the Insurance category.
    'gtm.element.dataset.productCategory': ESERVICE_GA4_BRAND,
  });
}

/** Input for {@link dataLayerPushRemoveEService}. */
export interface RemoveEServiceInput {
  /** The insurance id ({@link eServiceInsuranceId}) — the reported {@code item_id}. */
  insuranceId: string;
  /** The e-service display name — the reported {@code item_name}. */
  displayName: string;
  /** Final UNIT price of the e-service (0 when it comes free with the product). */
  unitPrice: number;
  /** Quantity removed — the attached product line's quantity, as legacy reports it. */
  quantity: number;
  /** Display name of the device the e-service protects — the reported {@code item_variant}. */
  attachedProductName: string;
}

/**
 * Pushes {@code remove_from_cart} for ONE detached e-service, mirroring legacy
 * {@code removeItemFromMiniCart(id, 'service')} (touch.js 6309): both the cart page's
 * e-service toggle-off and the mini-cart's e-service trash route through that same
 * legacy function, so both report the item the same way — the e-service as the item,
 * {@link ESERVICE_GA4_BRAND} as brand and category, and the device it protects as the
 * variant. Legacy's fixed title "Delete Service/s from Mini Cart" is rendered in Greek
 * with singular/plural agreement, matching the product-line titles the mini-cart
 * already pushes.
 *
 * @param input the detached e-service and the line it was attached to
 */
export function dataLayerPushRemoveEService(input: RemoveEServiceInput): void {
  dataLayerPushRemoveFromCart({
    title: input.quantity > 1
      ? 'Διαγραφή υπηρεσιών από Mini Cart'
      : 'Διαγραφή υπηρεσίας από Mini Cart',
    lines: [{
      itemId: input.insuranceId,
      itemName: input.displayName,
      itemBrand: ESERVICE_GA4_BRAND,
      itemCategory: ESERVICE_GA4_BRAND,
      color: input.attachedProductName,
      unitPrice: input.unitPrice,
      quantity: input.quantity,
      // The reported item IS the e-service, so the field marks it as such — the same
      // value legacy stamps on an e-service add — rather than listing attachments.
      eservices: 'non-variant',
    }],
  });
}

/** Input for {@link dataLayerPushViewCart}. */
export interface ViewCartInput {
  /** Every mini-cart line (legacy {@code productInfoElements} of the mini-cart). */
  lines: CartLineGa4[];
  /**
   * The cart {@code value} — legacy uses the displayed cart total (not the summed
   * line prices). The storefront passes its {@code cartTotal} (total ?? subtotal).
   */
  value: number;
  /** GTM {@code gtm.element.title} (legacy {@code title} = the button's text, e.g. "Προβολή καλαθιού"). */
  title: string;
}

/**
 * Pushes the legacy-compatible GA4 {@code view_cart} event, mirroring
 * {@code seeYourCart} in legacy touch.js: {@code ecommerce} reset to {@code null}
 * first, then a single {@code view_cart} payload with the {@code gtm.element.title}
 * (the button name) plus the {@code ecommerce} object ({@code currency}/{@code items}/
 * {@code value}). Each item carries the floored UNIT {@code price}, per-unit
 * {@code discount}, the line {@code quantity}, and {@code item_variant}. Unlike the
 * other cart events, {@code value} is the cart total supplied by the caller (legacy
 * read the displayed total), NOT the summed line prices. No-op during SSR.
 *
 * @param input the mini-cart line(s) + cart total + button title
 */
export function dataLayerPushViewCart(input: ViewCartInput): void {
  if (typeof window === 'undefined') {
    return;
  }
  const items = input.lines.map(mapCartLineToGa4Item);

  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, { ecommerce: null });
  safePush(w.dataLayer, {
    event: 'view_cart',
    'gtm.element.title': input.title,
    ecommerce: {
      currency: 'EUR',
      items,
      value: Number(input.value.toFixed(2)),
    },
  });
}

/** Input for {@link dataLayerPushBeginCheckout}. */
export interface BeginCheckoutInput {
  /** Every cart line (legacy filtered out the separate e-service rows; our cart model
   *  keeps e-services nested inside the product line, so all cart items qualify). */
  lines: CartLineGa4[];
  /**
   * The checkout {@code value} — legacy reads the final cart amount
   * ({@code #finalAmount2}), NOT the summed line prices. The storefront passes its
   * {@code cartTotal} (total ?? subtotal).
   */
  value: number;
}

/**
 * Pushes the legacy-compatible GA4 {@code begin_checkout} event, mirroring the cart.js
 * checkout-button handler: {@code ecommerce} reset to {@code null} first, then a single
 * {@code begin_checkout} payload with the {@code ecommerce} object ({@code currency}/
 * {@code items}/{@code value}). Unlike {@code view_cart} there is NO {@code title} — the
 * legacy push and the GTM "GA4 Begin Checkout" tag carry only {@code currency}/{@code value}/
 * {@code items}. Items use the floored UNIT {@code price} / per-unit {@code discount} /
 * line {@code quantity}; {@code value} is the caller-supplied cart total. No-op during SSR.
 *
 * @param input the cart line(s) + cart total
 */
export function dataLayerPushBeginCheckout(input: BeginCheckoutInput): void {
  if (typeof window === 'undefined') {
    return;
  }
  const items = input.lines.map(mapCartLineToGa4Item);

  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, { ecommerce: null });
  safePush(w.dataLayer, {
    event: 'begin_checkout',
    ecommerce: {
      currency: 'EUR',
      items,
      value: Number(input.value.toFixed(2)),
    },
  });
}

/** Legacy purchase {@code sticky}/{@code action} literal (touch.js Thank-You handler). */
const PURCHASE_STICKY = "The function isn't available for this event";

/**
 * Maps ONE purchased line to the legacy GA4 {@code purchase} item shape (touch.js
 * Thank-You handler): floored UNIT {@code price}, per-unit {@code discount}
 * ({@code reference − unit}), the line {@code quantity}, {@code item_list_name}
 * {@code 'Purchased Items'}, and the legacy {@code 'non-variant'} fallback for
 * {@code item_variant} / {@code item_brand} / {@code item_category}. Unlike the legacy
 * code, {@code item_id} is kept RAW (no {@code toUpperCase}) so the purchase reports the
 * SAME SKU id the rest of the funnel used (view → select → add → cart → begin_checkout →
 * purchase) — GA4 stitches the funnel on {@code item_id}.
 *
 * @param line the purchased cart line
 * @return the mapped GA4 purchase item
 */
function mapPurchaseLineToGa4Item(line: CartLineGa4): Record<string, unknown> {
  const unitPrice = Number((Math.floor(line.unitPrice * 100) / 100).toFixed(2));
  const reference = typeof line.referenceUnitPrice === 'number' ? line.referenceUnitPrice : unitPrice;
  const discount = Number((Math.floor((reference - line.unitPrice) * 100) / 100).toFixed(2));
  return {
    price: unitPrice,
    index: String(line.index ?? '0'),
    eservices: line.eservices || 'not-selected',
    item_name: line.itemName || '',
    item_list_name: 'Purchased Items',
    quantity: line.quantity,
    item_id: line.itemId || '',
    item_variant: line.color ? line.color : 'non-variant',
    item_brand: line.itemBrand ? line.itemBrand : 'non-variant',
    item_category: line.itemCategory ? line.itemCategory : 'non-variant',
    discount,
  };
}

/** Input for {@link dataLayerPushPurchase}. */
export interface PurchaseInput {
  /** GA4 {@code transaction_id} — the order id (legacy {@code #orderId}). */
  transactionId: string;
  /** Purchased lines ({@code item_id} = SKU id, for end-to-end funnel consistency). */
  lines: CartLineGa4[];
  /** Order revenue total (legacy {@code #pprice_grandtotal}). */
  value: number;
  /** Shipping cost (legacy {@code #shipping}). */
  shipping?: number;
  /** Legacy {@code data-isInvoice} (invoice vs receipt). */
  isInvoice?: boolean | string;
  /** {@code 'Store Pickup'} or {@code 'Home Delivery'} (legacy {@code #isStorePickup}). */
  deliveryType?: string;
  /** Payment method label (legacy {@code #paymentMethod}). */
  paymentMethod?: string;
  /** AADE art.39a eligibility flag (legacy {@code #orderSubmittedWithEligibleItemForArt39a}). */
  aade39aSubmittedOrder?: boolean | string;
}

/**
 * Pushes the legacy-compatible GA4 {@code purchase} event, mirroring the touch.js
 * Thank-You handler: {@code ecommerce} reset to {@code null} first, then a single
 * {@code purchase} payload with the {@code gtm.element.*} keys the GTM "GA4 Purchase" tag
 * reads ({@code title}/{@code sticky}/{@code action}/{@code isInvoice}/{@code deliveryType}/
 * {@code paymentMethod}/{@code binkies3DVariantA|B}/{@code aade39aSubmittedOrder}) plus the
 * NESTED {@code ecommerce.purchase} object ({@code currency}/{@code items}/{@code value}/
 * {@code shipping}/{@code transaction_id}). The Binkies 3D variants are not used on the new
 * site, so the legacy {@code 'Empty'} default is sent. The caller is responsible for firing
 * this exactly once per order (payment-confirmed + deduped). No-op during SSR.
 *
 * @param input the order + purchased line(s) for the purchase action
 */
export function dataLayerPushPurchase(input: PurchaseInput): void {
  if (typeof window === 'undefined') {
    return;
  }
  const items = input.lines.map(mapPurchaseLineToGa4Item);

  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, { ecommerce: null });
  safePush(w.dataLayer, {
    event: 'purchase',
    'gtm.element.title': 'Purchase',
    'gtm.element.dataset.sticky': PURCHASE_STICKY,
    'gtm.element.dataset.action': PURCHASE_STICKY,
    'gtm.element.dataset.isInvoice': input.isInvoice ?? false,
    'gtm.element.dataset.deliveryType': input.deliveryType ?? '',
    'gtm.element.dataset.paymentMethod': input.paymentMethod ?? '',
    'gtm.element.dataset.binkies3DVariantB': 'Empty',
    'gtm.element.dataset.binkies3DVariantA': 'Empty',
    'gtm.element.dataset.aade39aSubmittedOrder': input.aade39aSubmittedOrder ?? false,
    ecommerce: {
      purchase: {
        currency: 'EUR',
        items,
        value: Number(input.value.toFixed(2)),
        shipping: Number((input.shipping ?? 0).toFixed(2)),
        transaction_id: input.transactionId,
      },
    },
  });
}

/** Extra legacy {@code data-*} fields some PDP-navigating surfaces stamp. */
export interface ViewItemLinkInput extends ViewItemInput {
  /** Screen size (legacy {@code data-screen-size}). */
  screenSize?: string;
  /** Availability label (legacy {@code data-availability}, e.g. {@code inStock}). */
  availability?: string;
}

/** Result of {@link buildViewItemLink}: legacy {@code data-*} attrs + a click pusher. */
export interface ViewItemLink {
  /** Spread onto the navigating element (legacy {@code data-*} parity). */
  attrs: Record<string, string | number>;
  /** Call from the element's {@code onClick} to push the {@code view_item} event. */
  fire: () => void;
}

/**
 * Builds the legacy-parity {@code data-*} attributes plus a {@code fire()} click
 * pusher for ANY link that navigates the user to a PDP (mini-cart, cart line,
 * cross-sell, etc.). Mirrors the {@code ga4LinkProps} pattern already used by
 * {@code components/sections/ProductCard.tsx} so every PDP-navigating surface
 * emits the identical {@code view_item} event shape (legacy {@code dataLayerPushViewItem}
 * parity). Surfaces compose {@code fire()} into their own click handler so any
 * existing behaviour (e.g. closing the mini-cart) is preserved.
 *
 * @param input product + list context for the clicked link
 * @return the {@code data-*} attrs to spread and the {@code fire()} pusher
 */
export function buildViewItemLink(input: ViewItemLinkInput): ViewItemLink {
  return {
    attrs: {
      'data-quantity': input.quantity ?? 1,
      'data-section': input.listName,
      'data-product-id': input.productId,
      'data-product-name': input.productName,
      'data-product-brand': input.brand ?? '',
      'data-product-category': input.category ?? '',
      'data-screen-size': input.screenSize ?? '',
      'data-color': input.color ?? '',
      'data-position': input.position ?? 0,
      'data-availability': input.availability ?? '',
      'data-price': input.price,
      'data-promo-price': input.promoPrice,
    },
    fire: () => dataLayerPushViewItem(input),
  };
}

/**
 * Legacy {@code sticky}/{@code action} literal stamped on the {@code view_item_list}
 * push — touch.js sets {@code sticky = action = "The function isn't available for
 * this event"} because list impressions have no originating sticky element.
 */
const VIEW_ITEM_LIST_NA = "The function isn't available for this event";

/**
 * Pushes the legacy-compatible GA4 {@code view_item_list} (list-impression) event,
 * mirroring {@code dataLayerPushViewItemList(cT)} in legacy touch.js.
 *
 * <p>Reads the product tiles straight from the {@code container}'s DOM — every
 * {@code [data-product-id]} element that is NOT a colour swatch
 * ({@code :not(.available-colors)}, legacy parity) — so it reuses the exact same
 * {@code data-*} attributes {@link buildViewItemLink} already stamps for the
 * {@code view_item}/{@code add_to_cart} events (single inspectable source on the DOM).
 *
 * <p>Per-tile item shape matches legacy: {@code quantity 1}, floored UNIT
 * {@code price} ({@code data-promo-price}), per-unit {@code discount}
 * ({@code data-price − data-promo-price}), {@code item_list_name} (full
 * {@code data-section}), {@code index} ({@code data-position}) and the
 * {@code 'non-variant'} colour fallback. Consecutive tiles sharing a product id are
 * de-duplicated (legacy dropped the repeated colour-variant rows of one product).
 * Unlike legacy, {@code item_id} is the RAW value (no {@code toUpperCase}) so the
 * list reports the SAME SKU id the rest of the funnel uses (view → select → add →
 * cart → begin_checkout → purchase). The {@code gtm.element.title} is the
 * {@code data-section} trimmed at {@code ' -'} (legacy {@code substr} rule). No-op
 * during SSR or when the container holds no product tiles.
 *
 * @param container the list/carousel root element holding the product tiles
 */
export function dataLayerPushViewItemList(container: HTMLElement | null): void {
  if (typeof window === 'undefined' || !container) {
    return;
  }

  const tiles = Array.from(
    container.querySelectorAll<HTMLElement>('[data-product-id]:not(.available-colors)'),
  );

  const items: Array<Record<string, unknown>> = [];
  let prevId = '';
  let listName = '';
  for (const el of tiles) {
    const d = el.dataset;
    const id = d.productId ?? '';
    // Legacy de-dupes only CONSECUTIVE repeats (a product's colour variants are
    // emitted back-to-back), so a product re-appearing later still counts.
    if (!id || id === prevId) {
      continue;
    }
    prevId = id;
    if (!listName) {
      listName = d.section ?? '';
    }
    const fullPrice = Number(d.price ?? 0);
    const promoPrice = Number(d.promoPrice ?? 0);
    const unitPrice = Number((Math.floor(promoPrice * 100) / 100).toFixed(2));
    items.push({
      quantity: 1,
      price: unitPrice,
      item_list_name: d.section ?? '',
      index: d.position ?? 0,
      item_name: d.productName ?? '',
      item_brand: d.productBrand ?? '',
      item_category: d.productCategory ?? '',
      item_id: id,
      item_variant: d.color ? d.color : 'non-variant',
      discount: Number((fullPrice - promoPrice).toFixed(2)),
    });
  }

  if (items.length === 0) {
    return;
  }

  const dashIdx = listName.indexOf(' -');
  const title = dashIdx >= 0 ? listName.substring(0, dashIdx) : listName;

  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, { ecommerce: null });
  safePush(w.dataLayer, {
    event: 'view_item_list',
    'gtm.element.title': title,
    'gtm.element.dataset.sticky': VIEW_ITEM_LIST_NA,
    'gtm.element.dataset.action': VIEW_ITEM_LIST_NA,
    ecommerce: {
      items,
      currency: 'EUR',
    },
  });
}

/** The three legacy menu-item data-* attributes read by the GTM "GA4 Menu Item" tag. */
export interface MenuItemAttrs {
  'data-type': string;
  'data-section': string;
  'data-site-tree-path': string;
}

/**
 * Builds the legacy mega-menu navigation data-* attributes (topBar.jsp /
 * navigationMenu.jsp parity) to stamp on a clicked menu link. The GTM
 * "GA4 Menu Item" tag reads these off the clicked element:
 * {@code data-section} → {@code title}, {@code data-type} → {@code menu_item_type},
 * {@code data-site-tree-path} → {@code site_tree_path}.
 *
 * @param siteTreePath slash-joined category path, e.g. {@code "Τηλεφωνία & Tablets/Κινητά/Smartphones"}
 * @param section the clicked item's own label (legacy {@code data-section})
 * @param type menu item type; legacy menus always used {@code "Products"} (the default)
 * @return the spreadable data-* attribute object
 */
export function buildMenuItemAttrs(siteTreePath: string, section: string, type = 'Products'): MenuItemAttrs {
  return {
    'data-type': type,
    'data-section': section,
    'data-site-tree-path': siteTreePath,
  };
}

/**
 * Pushes the legacy GA4 mega-menu navigation event
 * ({@code dataLayer.push({'event':'Click_Menu_Item'})} from topBar.jsp /
 * navigationMenu.jsp). Fire it from a menu link's {@code onClick}, passing
 * {@code e.currentTarget} (the link element, NOT the physically-clicked node).
 *
 * <p>The GTM "GA4 Menu Item" tag resolves its {@code Data Attribute Section /
 * Type / Site Tree Path} variables from {@code {{Click Element}}} (i.e.
 * {@code gtm.element}). When a click lands on an inner {@code <span>}/{@code <div>}
 * of the link (sub/child category links, service cards), {@code gtm.element}
 * would be that inner node — which has no {@code data-*} — so the tag reads empty
 * values; only links whose label is a direct text child of the {@code <a>} (the
 * main category links) resolved correctly. Pinning {@code gtm.element} to the
 * link element here makes EVERY level resolve the {@code data-*} stamped by
 * {@link buildMenuItemAttrs}, regardless of where the click landed. No-op during SSR.
 *
 * @param element the menu link element carrying the {@code data-*} attributes
 *        (pass {@code e.currentTarget} from the click handler)
 */
export function dataLayerPushClickMenuItem(element?: HTMLElement | null): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  const payload: Record<string, unknown> = { event: 'Click_Menu_Item' };
  if (element) {
    // GTM {{Click Element}} is the Data Layer Variable `gtm.element`; pinning it
    // to the link guarantees the element-attribute variables read THIS node's data-*.
    payload['gtm.element'] = element;
  }
  safePush(w.dataLayer, payload);
}

/**
 * Pushes the legacy GA4 header main-menu event
 * ({@code dataLayer.push({'event':'Click_Main_Menu'})} from touch.js
 * {@code dataLayerPushClickMainMenu}). Fire it when a HEADER menu is opened or
 * closed — the Product/Service mega-menu and the profile/account menu. (Clicks
 * on individual menu items are tracked by {@link dataLayerPushClickMenuItem}.)
 *
 * <p>The GTM "GA4 Menu" tag reads {@code title} ← {@code {{Data Attribute Section}}},
 * {@code position} ← {@code {{Data Attribute Position}}}, {@code action} ←
 * {@code {{Data Attribute Action}}}. Legacy pushed these as
 * {@code gtm.element.dataset.*} dataLayer keys (NOT off a DOM node), so we
 * replicate that exact shape. No-op during SSR.
 *
 * @param action  {@code "Open"} or {@code "Close"}
 * @param section the menu's title/label (becomes the GTM tag's {@code title}),
 *        e.g. {@code "Μενού προϊόντων και υπηρεσιών"} or {@code "Profile Menu"}
 * @param position menu location; legacy always used {@code "Header"} (the default)
 */
export function dataLayerPushClickMainMenu(
  action: 'Open' | 'Close',
  section: string,
  position = 'Header',
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Click_Main_Menu',
    'gtm.element.dataset.action': action,
    'gtm.element.dataset.section': section,
    'gtm.element.dataset.position': position,
  });
}

/**
 * Pushes the legacy GA4 button event ({@code dataLayerPushClickButton} in
 * touch.js). Fire it from any control that reads as a button in the UI —
 * primary/secondary CTAs, modal confirm/cancel pairs, close buttons — but NOT
 * from controls that already emit their own commerce event (add-to-cart,
 * checkout, remove) or from tabs, which use {@code Click_Tab}.
 *
 * <p>The GTM "GA4 Button" tag fires on the {@code Click_Button} custom event and
 * maps {@code title} ← {@code {{Title Attribute}}} and {@code action} ←
 * {@code {{Data Attribute Action}}}; {@code page_url} comes from {@code {{Page URL}}}
 * so it is not pushed here. Legacy sent these as {@code gtm.element.*} dataLayer
 * keys (NOT off a DOM node), so we replicate that exact shape. No-op during SSR.
 *
 * @param title  the button's visible label, taken VERBATIM from what the markup
 *        renders — e.g. {@code "ΜΑΘΕ ΠΕΡΙΣΣΟΤΕΡΑ"}. For icon-only buttons use the
 *        accessible name. Never invent a title; it follows the UI, so it is
 *        usually Greek. It may be a composite that scopes the label to its area
 *        ({@code "Αφαίρεση - Δώρο"}, {@code "Εξαργύρωση - Coupon Form"}): keep the
 *        frame Greek when it too comes from the markup — a heading, an aria-label,
 *        visible text — and English only when you are authoring it yourself.
 * @param action the description WE author, and the only field that must be kept
 *        aligned: always English, drawn from one shared verb set reused across
 *        every event ({@code "Open"}, {@code "Close"}, {@code "Show More"},
 *        {@code "Clear Cart"}, …), since this is what you group and compare on.
 *        Use the destination URL instead when the button navigates away. Do not
 *        encode a varying value here — put it in the title and keep the action
 *        stable, the way the gallery thumbnails pass {@code "Change Main Image"}.
 */
export function dataLayerPushClickButton(title: string, action: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Click_Button',
    'gtm.element.title': title,
    'gtm.element.dataset.action': action,
  });
}

/**
 * Pushes the GA4 mega-menu promo banner event. Fire it from the banner link's
 * {@code onClick}.
 *
 * <p>Legacy never had a code path for this: the banner slots (BCC
 * {@code menuItemDA} / {@code DA2} / {@code DA3}) were authored as CMS HTML and
 * each carried its own inline {@code onclick="dataLayer.push(…)"}, the shape the
 * GA4 transition XML shipped. The current banner HTML carries no such push, so
 * the HTML slots report through {@code trackMenuBannerClick} and the image-only
 * fallback calls this directly.
 *
 * <p>The GTM "GA4 Menu Banner" tag reads {@code title} ←
 * {@code {{Title Attribute}}}, {@code destination_url} ←
 * {@code {{Data Attribute Destination URL}}} and {@code position} ←
 * {@code {{Data Attribute Position}}}, so those are pushed as the flat
 * {@code gtm.element.*} keys the same way {@link dataLayerPushClickMainMenu}
 * does. No-op during SSR.
 *
 * @param title          the banner's accessible name — its {@code alt} text
 * @param destinationUrl where the banner takes the user
 * @param position       1-based slot in the banner stack
 */
export function dataLayerPushClickMenuBanner(
  title: string,
  destinationUrl: string,
  position: number,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Click_Menu_Banner',
    'gtm.element.title': title,
    'gtm.element.dataset.destinationUrl': destinationUrl,
    'gtm.element.dataset.position': String(position),
  });
}

/** Input for {@link dataLayerPushAddToWishlist}. */
export interface AddToWishlistInput {
  /**
   * GTM {@code gtm.element.title} — the heart's on-screen label, sent as a label
   * only. It is translated copy, so it must never be what a report keys on; use
   * {@link AddToWishlistInput#action} for that.
   */
  title: string;
  /**
   * {@code 'select'} when the product enters the wishlist, {@code 'reject'} when it
   * leaves it. Legacy derived this from the heart's {@code removeFromWishList}
   * class; here it comes from the same {@code inWishlist} flag that fills the icon.
   */
  action: 'select' | 'reject';
  /** GA4 {@code item_id} — the displayed SKU id, product id only as a fallback. */
  productId: string;
  productName: string;
  /** Full / reference UNIT price. Used only to derive the per-unit discount. */
  price: number;
  /** Final / promo UNIT price — becomes the GA4 {@code price} and {@code value}. */
  promoPrice: number;
  brand?: string;
  category?: string;
  /** Colour description; empty → {@code 'non-variant'} (legacy parity). */
  color?: string;
  /** 0-based position within the surrounding list. */
  position?: number | string;
}

/**
 * Pushes the GA4 {@code add_to_wishlist} event for a wishlist toggle. This is
 * Google's recommended ecommerce event rather than legacy's custom
 * {@code Add_Favorite_Product}, so it carries the standard
 * {@code currency}/{@code value}/{@code items} payload and needs no custom
 * dimensions to report.
 *
 * <p>ONE event covers both directions: {@link AddToWishlistInput#action} carries
 * {@code 'select'} for an add and {@code 'reject'} for a removal, the same
 * select/reject pairing legacy used and the comparison events still use. Reports
 * MUST split on that parameter — under Google's own semantics the bare event name
 * means an addition, so an unsegmented count includes removals.
 *
 * <p>Fire it only AFTER the wishlist API confirms the change, never on the click
 * — the same rule {@code add_to_cart} follows. Legacy pushed on click and so
 * counted failed adds; that is a deliberate divergence.
 *
 * <p>Pricing follows the project-wide rule: {@code price} and {@code promoPrice}
 * are per-unit, the GA4 {@code price} is the floored final unit price and
 * {@code discount} is {@code price - promoPrice}. No-op during SSR.
 *
 * @param input the product snapshot plus the direction of the change
 */
export function dataLayerPushAddToWishlist(input: AddToWishlistInput): void {
  if (typeof window === 'undefined') {
    return;
  }
  const discount = Number((input.price - input.promoPrice).toFixed(2));
  const unitPrice = Number((Math.floor(input.promoPrice * 100) / 100).toFixed(2));

  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, { ecommerce: null });
  safePush(w.dataLayer, {
    event: 'add_to_wishlist',
    'gtm.element.title': input.title,
    'gtm.element.dataset.action': input.action,
    ecommerce: {
      currency: 'EUR',
      value: unitPrice,
      items: [
        {
          price: unitPrice,
          discount,
          index: input.position ?? 0,
          item_id: input.productId,
          quantity: 1,
          item_name: input.productName,
          item_brand: input.brand || '',
          item_category: input.category || '',
          item_variant: input.color ? input.color : 'non-variant',
        },
      ],
    },
  });
}

/** Product snapshot behind the two comparison events. */
export interface CompareProductGa4 {
  /** GA4 {@code item_id} — the displayed SKU id, product id only as a fallback. */
  productId: string;
  productName: string;
  /** Full / reference UNIT price. Used only to derive the per-unit discount. */
  price: number;
  /** Final / promo UNIT price — becomes the GA4 {@code price} and {@code value}. */
  promoPrice: number;
  brand?: string;
  category?: string;
  /** Colour description; empty → {@code 'non-variant'} (legacy parity). */
  color?: string;
  /** 0-based position within the surrounding list. */
  position?: number | string;
}

/** Maps ONE compared product to the GA4 ecommerce item shape. */
function mapCompareProductToGa4Item(product: CompareProductGa4): Record<string, unknown> {
  return {
    price: Number((Math.floor(product.promoPrice * 100) / 100).toFixed(2)),
    discount: Number((product.price - product.promoPrice).toFixed(2)),
    index: product.position ?? 0,
    item_id: product.productId,
    quantity: 1,
    item_name: product.productName,
    item_brand: product.brand || '',
    item_category: product.category || '',
    item_variant: product.color ? product.color : 'non-variant',
  };
}

/**
 * Builds and pushes one comparison event. The dataLayer event name must stay
 * exactly as written: the GTM triggers match {@code {{_event}}} against
 * {@code add_to_compare} / {@code remove_from_compare}, and the tags send those
 * same names on to GA4 — the {@code add_to_cart}/{@code remove_from_cart}
 * convention, where the dataLayer name and the GA4 name are one and the same.
 * Legacy's {@code Add_Compare_Product}/{@code Remove_Compare_Product} names are
 * retired.
 *
 * <p>Takes a LIST because "clear all" empties the comparison in one click and
 * reports every product it dropped as a single remove; the per-product controls
 * simply pass one.
 */
function pushCompareEvent(
  event: 'add_to_compare' | 'remove_from_compare',
  title: string,
  products: CompareProductGa4[],
): void {
  if (typeof window === 'undefined' || products.length === 0) {
    return;
  }
  const items = products.map(mapCompareProductToGa4Item);
  const value = Number(
    items.reduce((sum, item) => sum + (item.price as number), 0).toFixed(2),
  );

  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, { ecommerce: null });
  safePush(w.dataLayer, {
    event,
    'gtm.element.title': title,
    ecommerce: { currency: 'EUR', value, items },
  });
}

/**
 * Pushes the comparison add event, reported to GA4 as {@code add_to_compare}.
 *
 * <p>Google has no standard comparison event, so this is a custom event modelled
 * on a standard one: snake_case name, the standard ecommerce payload
 * ({@code currency}/{@code value}/{@code items}), and no custom dimensions —
 * rather than legacy's thirteen flat {@code product_*} parameters. Because it is
 * not a recognised Google event, the items populate item-scoped dimensions but
 * do not feed the built-in ecommerce reports.
 *
 * <p>Add and remove are two separate events here, mirroring
 * {@code add_to_cart}/{@code remove_from_cart}. Legacy instead fired one event
 * for both directions and split on a select/reject action
 * ({@code touch.js:4749}); that is a deliberate divergence.
 *
 * @param title   the control's visible label
 * @param product the product snapshot for the compared product
 */
export function dataLayerPushAddToCompare(title: string, product: CompareProductGa4): void {
  pushCompareEvent('add_to_compare', title, [product]);
}

/**
 * Pushes the comparison remove event, reported to GA4 as
 * {@code remove_from_compare}. Every control that drops products reports here:
 * the card and PDP toggles, the tray's "×", the compare page's "×", and "clear
 * all" — which passes the whole comparison, so emptying the tray is ONE remove
 * carrying every item rather than a burst of single-item events.
 *
 * <p>Legacy pushed this event with NO payload at all
 * ({@code touch.js:4119}), so its tag's {@code product_name} and
 * {@code position} were always empty in production. Sending the items fixes
 * that. Legacy's separate {@code Click_Clear_Compare} event is retired: clearing
 * is a removal, just of everything at once.
 *
 * @param title    the control's visible label
 * @param products every product being dropped; a no-op when empty
 */
export function dataLayerPushRemoveFromCompare(
  title: string,
  products: CompareProductGa4[],
): void {
  pushCompareEvent('remove_from_compare', title, products);
}

/**
 * Pushes the legacy GA4 tab event. Fire it when the user switches a tab — a
 * control that swaps the content below it without navigating away (mega-menu
 * Προϊόντα/Υπηρεσίες, PDP Επισκόπηση/Χαρακτηριστικά, special-product tab
 * strips, store picker list/map).
 *
 * <p>Unlike {@code Click_Button}, {@code Click_Tab} is NOT a custom event name:
 * legacy marked up tabs as {@code <a data-click-event="Click_Tab" name=…
 * data-position=… data-section=…>} and let GTM's native link-click listener
 * emit {@code gtm.linkClick} (productDetailsTop.jsp, hubPageSpecialProducts.jsp,
 * orderTracking.jsp). Where the tab was not an anchor, legacy pushed that same
 * shape by hand ({@code tabPDPMap} / {@code tabPDPStore} in deliveryMethods.js).
 * Our tabs are {@code <button>}s, which the link-click listener never sees, so
 * we take the manual path for all of them.
 *
 * <p>{@code gtm.triggers} is pushed EMPTY rather than legacy's hardcoded trigger
 * id: every germanos click trigger carries the filter
 * {@code {{gtm.triggers}} matches RegEx (^$|…)}, so an empty value satisfies the
 * {@code ^$} branch and lets {@code data-click-event} remain the sole
 * discriminator. This is the same convention {@code gtmClickBridge.ts} already
 * ships. It is also reset explicitly so a stale value from a previous native
 * click cannot linger on the shared dataLayer. No-op during SSR.
 *
 * @param title    the tab's visible label, e.g. {@code "Χαρακτηριστικά"}
 * @param position the tab's zero-based index within its strip
 * @param section  the tab strip's name, e.g. {@code "PDP Features & Specs"}
 */
export function dataLayerPushClickTab(
  title: string,
  position: number,
  section: string,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'gtm.linkClick',
    'gtm.triggers': '',
    'gtm.element.name': title,
    'gtm.element.dataset.clickEvent': 'Click_Tab',
    'gtm.element.dataset.position': String(position),
    'gtm.element.dataset.section': section,
  });
}

/**
 * GA4 `Click_Link` title for the site logo, which returns the user home.
 * Legacy passed its `homePageTitle` CMS message here; this is the same string
 * the breadcrumbs already report for home, so the two agree in reports.
 */
export const HOME_LINK_TITLE = 'Αρχική Σελίδα';

/**
 * Pushes the legacy GA4 link event for a navigation that JavaScript performs
 * rather than an {@code href}.
 *
 * <p><b>Most links must NOT call this.</b> The GTM "Click Link" trigger is a
 * Just Links trigger, and GTM's own listener runs on the top document — so a
 * real {@code <a data-click-event="Click_Link" title="…">} in the light DOM
 * already reports itself from markup alone. Calling this as well would count
 * the click twice. Reach for it only where a Just Links trigger can never see
 * the control: a {@code <button>} that navigates, a handler that calls
 * {@code navigate()}, or a form submit standing in for a link.
 *
 * <p>Legacy did exactly this, and for the same reason — the Cosmote ID
 * registration redirect built a form and pushed by hand before
 * {@code form.submit()} (cosmoteId-login.js), while the header and checkout
 * logos pushed from {@code onclick} so the report survived the page unload
 * (topBar.jsp, checkoutMainPage.jsp, newCartActionsMenu.jsp).
 *
 * <p>The URL is required here, unlike the markup path. With no element pinned
 * to the event, GTM cannot read an {@code href} of its own, and
 * {@code gtm.elementUrl} is what backs the built-in {@code {{Click URL}}} the
 * tag sends as {@code destination_url}. {@code gtm.triggers} is pushed empty so
 * the {@code ^$} branch of every click trigger's filter passes and
 * {@code data-click-event} stays the only discriminator — the convention
 * {@code gtmClickBridge.ts} already ships. No-op during SSR.
 *
 * @param title the link's visible label, e.g. {@code "Συνέχεια Αγορών"}
 * @param url   where the click takes the user
 */
export function dataLayerPushClickLink(title: string, url: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'gtm.linkClick',
    'gtm.triggers': '',
    'gtm.element.dataset.clickEvent': 'Click_Link',
    'gtm.element.title': title,
    'gtm.elementUrl': url,
  });
}

/**
 * Pushes the legacy GA4 dropdown-choice event. Fire it when the user PICKS a
 * row out of an open dropdown/listbox — installment plans, loyalty coupons,
 * address suggestions. The control that opens the list is a button and takes
 * {@link dataLayerPushClickButton} with {@code "Open"}/{@code "Close"} instead.
 *
 * <p>Legacy never wrote this by hand: it swept the ddslick anchors on ready,
 * stamped each one with {@code data-section} = the option's visible text, and
 * hung an inline {@code onclick="dataLayer.push({'event':'Click_DropDown_Choice'})"}
 * on it (main.js:149, hana_coupons.js:415, pickup_return.js:344, plus the footer
 * newsletter list). Where a list was nested under another control it scoped the
 * value with a {@code " - <parent>"} suffix. Our rows are React buttons rather
 * than ddslick anchors, so — exactly as {@link dataLayerPushClickMainMenu} does —
 * we push the {@code gtm.element.dataset.*} shape the GTM tag reads directly.
 * No-op during SSR.
 *
 * @param section the value that was picked, verbatim as the row renders it, and
 *        optionally scoped to the list it came from the way legacy did
 *        ({@code "3 Δόσεις"}, {@code "Ερμού 12, Αθήνα - Address Suggestions"})
 */
export function dataLayerPushClickDropDownChoice(section: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Click_DropDown_Choice',
    'gtm.element.dataset.section': section,
  });
}

/**
 * Pushes the legacy GA4 checkbox event. Fire it when the user ticks or unticks
 * a checkbox — consents, terms, gift wrapping, save-my-card, newsletter topics.
 * A checkbox is NOT a button: it has two states, and the state it landed in is
 * the whole point, which is why this carries an action where
 * {@link dataLayerPushClickDropDownChoice} does not.
 *
 * <p>Legacy pushed this from a dozen change handlers (checkout-ui.js:277/448/1340,
 * checkout-ui-maps-2.js:23/2450/5092, cart.js:811, hana_coupons.js:37,
 * pickup_return.js:291/312). Every one of them built the section the same way:
 * the checkbox's own visible label, then {@code " - "}, then the form it lives
 * in — {@code "Αποδέχομαι τους όρους - Terms & Conditions Form"}. Keep that
 * shape so the rows group by form in the report.
 *
 * <p>The action vocabulary drifted in legacy (Selected/Rejected, select/reject,
 * Enabled/Disabled, Subscribe/Unsubscribe). Selected/Rejected is the dominant
 * pair and the one to use. No-op during SSR.
 *
 * @param section the checkbox's visible label suffixed with its form, legacy
 *        style ({@code "<label> - <Form Name>"})
 * @param selected whether the box ended up ticked — {@code true} pushes
 *        {@code "Selected"}, {@code false} pushes {@code "Rejected"}
 */
export function dataLayerPushClickCheckbox(section: string, selected: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Click_Checkbox',
    'gtm.element.dataset.section': section,
    'gtm.element.dataset.action': selected ? 'Selected' : 'Rejected',
  });
}

/**
 * Pushes the legacy GA4 radio event (checkout-ui.js:1497, 1548). Fire it when
 * the user picks one option out of a mutually-exclusive set, and only when the
 * pick actually changes the selection.
 *
 * <p>This is the UMBRELLA for the whole pick-one-from-a-set family (ruled
 * 2026-09-28). Legacy split the same interaction across three tags —
 * {@code Select_Radio_Button} for the invoice/receipt toggle,
 * {@code Select_Saved_Address} for the address boxes,
 * {@code Select_Saved_Card} for the card strip — which differed only in what was
 * being picked, never in how the control behaved. Those two tags are retired;
 * everything routes here.
 *
 * <p>Unlike {@link dataLayerPushClickCheckbox} there is no Rejected half: a
 * radio can only be turned on, since turning it off means turning another one
 * on. Legacy hardcoded {@code "Select"} for exactly that reason, so the action
 * is fixed here rather than exposed as a parameter.
 *
 * <p>NEVER pass user-typed text as the label. The address boxes carry a
 * customer-chosen alias which can hold a name, so those call sites pass the
 * ordinal ({@code "Address 2"}) instead — the same reason the address payload
 * was dropped from {@code Submit_Address_Info_Button}. No-op during SSR.
 *
 * @param label    what was picked, legacy style — the option's visible label
 *                 where it is safe to send, otherwise a positional stand-in
 * @param formName the form the control lives in, e.g.
 *                 {@code "Billing Info Form"}
 */
export function dataLayerPushSelectRadioButton(label: string, formName: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Select_Radio_Button',
    'gtm.element.dataset.action': 'Select',
    'gtm.element.dataset.section': `${label} - ${formName}`,
  });
}

/**
 * Pushes the legacy GA4 field-engagement event (personalData.jsp:81 and 55 more
 * sites). Fire it from the field's `onFocus` — whether the user arrived by
 * mouse or by Tab — so it answers one question: did anyone engage with this
 * field, or did they skip past it?
 *
 * <p>Focus is the whole guard. It fires on entry and not on any keystroke that
 * follows, and leaving the field is not an interaction. Focusing back in IS a
 * fresh interaction and reports again — a field people keep returning to is a
 * field that is giving them trouble, which is the signal worth having.
 *
 * <p>NEVER pass the value, its length, or a validity flag. Nearly every field
 * carrying this event holds personal data — names, emails, phones, addresses,
 * ΑΦΜ, IMEI — so the payload says "someone typed here", never "here is what
 * they typed". Legacy carried the section and nothing else for exactly this
 * reason.
 *
 * <p>Both halves of the section are ENGLISH even though the UI is Greek, which
 * is legacy's convention: «Όνομα» reports as {@code First Name}. Where a field
 * has a legacy twin, reuse its section verbatim so the two datasets line up.
 *
 * <p>Search boxes are excluded by ruling — they already carry
 * {@link dataLayerPushClickSearch}, which answers the same question with more
 * detail. No-op during SSR.
 *
 * <p>The push is shaped as a {@code gtm.click}, not as a custom event named
 * after the tag. Legacy never pushed this one at all: `main.js` stamped
 * `data-click-event` + `data-section` onto every textarea and let the published
 * "Click Field Text Area" trigger — an All Elements click trigger — read them,
 * so a custom event named `Click_Field_Text_Area` would match nothing. We
 * cannot go back to plain markup either, because a click trigger never sees a
 * field entered with the Tab key. {@link dataLayerPushClickTab} resolves the
 * same conflict the same way, with {@code gtm.linkClick} for its Just Links
 * trigger.
 *
 * <p>The tag also reads a {@code field_type} parameter from the element's own
 * `type` — `email`, `tel`, `textarea` and so on — which is why the focus event
 * has to come along. Passing the event rather than a literal keeps the
 * reported type honest if a field's `type` ever changes.
 *
 * @param event     the field's focus event
 * @param fieldName the field in English, e.g. {@code "First Name"}
 * @param formName  the form in English, e.g. {@code "Personal Data Form"}
 */
export function dataLayerPushClickFieldTextArea(
  event: { currentTarget?: { type?: string } | null },
  fieldName: string,
  formName: string,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'gtm.click',
    'gtm.triggers': '',
    'gtm.element.type': event.currentTarget?.type ?? '',
    'gtm.element.dataset.clickEvent': 'Click_Field_Text_Area',
    'gtm.element.dataset.section': `${fieldName} - ${formName}`,
  });
}

/** GA4 `Select_Radio_Button` form name for the store list tab. */
export const STORE_LIST_FORM = 'Store List Form';

/** GA4 `Select_Radio_Button` form name for the store map tab. */
export const STORE_MAP_FORM = 'Store Map Form';

/**
 * Builds the {@link dataLayerPushSelectRadioButton} label for a store, in the
 * shape the shopper reads it: «ΠΕΤΡΑΛΩΝΑ, ΘΕΣΣΑΛΟΝΙΚΗΣ 147».
 *
 * <p>Unlike the saved-address and saved-card call sites, this one sends the
 * real label rather than an ordinal: a shop's municipality and street are
 * public business data, not anything the customer typed. Which branches people
 * actually pick is the whole point of the event.
 *
 * <p>{@code area} is optional on the store record, so {@code storeName} — which
 * is not — covers the rows that arrive without one.
 *
 * @param store the chosen store
 * @return the label, never empty
 */
export function storeRadioLabel(store: {
  storeName: string;
  address: string;
  area?: string;
}): string {
  return store.area ? `${store.area}, ${store.address}` : store.storeName;
}

/**
 * What {@link dataLayerPushClickSearch} sends when the user submits an empty
 * box. An empty submit is still a user action worth counting — it usually means
 * the magnifier was mistaken for something else — but it must stay separable
 * from a real query or it inflates the search total. The parenthesised lowercase
 * form matches GA4's own system values ({@code (not set)}, {@code (direct)}), so
 * it reads naturally in reports and no real query can ever collide with it.
 */
export const EMPTY_SEARCH_VALUE = '(empty search)';

/** A standalone house number, optionally suffixed with a single letter (12, 12Α). */
const HOUSE_NUMBER = /(^|[\s,])\d{1,4}[A-Za-zΑ-Ωα-ωΆ-Ώά-ώ]?(?=$|[\s,])/g;

/** A Greek postal code as Google formats it inside an address — "546 23". */
const SPACED_POSTAL_CODE = /(^|[\s,])(\d{3})\s(\d{2})(?=$|[\s,])/g;

/**
 * Strips the house number out of a typed address while keeping everything that
 * is safe to report on. Location searches are the only place a GA4 payload can
 * pick up a street address, and the house number is the part that narrows a
 * street down to a household — the street and city on their own cover hundreds
 * of people and stay useful for "where are customers looking for a store".
 *
 * <p>Greek postal codes are exactly five digits, so the pattern deliberately
 * stops at four: {@code 16561} survives, {@code 43} does not. Street names that
 * begin with a number keep theirs, because the digits there carry a letter tail
 * that is part of the name ({@code 25ης Μαρτίου}). Google writes postal codes
 * split as {@code "546 23"} inside a formatted address, which would otherwise
 * read as two house numbers, so those are joined back up first.
 *
 * @param raw the string the user typed, or a Google formatted address
 * @return the same string without house numbers, punctuation tidied up
 */
export function sanitizeLocationQuery(raw: string): string {
  return raw
    .replace(SPACED_POSTAL_CODE, '$1$2$3')
    .replace(HOUSE_NUMBER, '$1')
    .replace(/\s+/g, ' ')
    .replace(/\s+,/g, ',')
    .replace(/,\s*(?=,)/g, '')
    .replace(/^[\s,]+|[\s,]+$/g, '');
}

/**
 * Pushes the legacy GA4 search event. Fire it once per search the user actually
 * performs — never per keystroke.
 *
 * <p>Legacy pushed this by hand from about a dozen handlers (touch.js:5473 the
 * header magnifier, main.js:254 the header Enter, main.js:231/3303 the
 * no-results box, touch.js:6262 the mic, plus the store and address pickers in
 * deliveryMethods.js and checkout-ui-maps-2.js). Every one of them sent the same
 * three keys, and the GTM tag still reads exactly those: {@code section} becomes
 * the report's {@code title}, {@code type} becomes {@code search_type} and
 * {@code searchText} becomes {@code search_value}.
 *
 * <p>The event is deliberately generic — the surface lives in {@code section},
 * so one tag covers catalog search, facet filtering and store lookup, and the
 * report segments by title ({@code "'Κατασκευαστής' - Facet Search"},
 * {@code "Store Locator - Location Search"}).
 *
 * <p>Callers on a box with a submit fire on the submit. Callers on a box with no
 * submit (facet filters, address autocomplete) go through
 * {@code useSearchCommit} so a search-as-you-type field still produces exactly
 * one event. No-op during SSR.
 *
 * @param section the surface the search happened on, suffixed legacy-style so
 *        the family groups in reports ({@code "<Surface> - Location Search"})
 * @param type how the search was triggered — {@code "magnifying-glass"},
 *        {@code "enter"}, {@code "mic"}, {@code "facet"}, or the location
 *        vocabulary {@code "Search Location"} / {@code "Selected Location"} /
 *        {@code "Current Location"}
 * @param searchText what was searched for; pass it through
 *        {@link sanitizeLocationQuery} first on any address-bearing surface.
 *        Blank becomes {@link EMPTY_SEARCH_VALUE}
 */
export function dataLayerPushClickSearch(section: string, type: string, searchText: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Click_Search',
    'gtm.element.dataset.section': section,
    'gtm.element.dataset.type': type,
    'gtm.element.dataset.searchText': searchText.trim() || EMPTY_SEARCH_VALUE,
  });
}

/**
 * Pushes the legacy GA4 delivery-method event (deliveryMethods.js:171, 360, 478,
 * 556, 626). Fire it when the shopper PICKS a delivery method, and only then.
 *
 * <p>Legacy guarded every push with {@code document.readyState === "complete"}
 * so the method auto-selected while the page booted stayed out of the report.
 * That guard does not translate: our effects re-pick the method long after load
 * — BOX NOW turning out ineligible, preorder restrictions, a cart refresh
 * flipping eligibility, a modal closed without a choice. So callers must fire
 * from the click handler rather than from wherever the method state settles, and
 * only when the method actually changed.
 *
 * <p>Unlike most of the family this one is keyed on {@code gtm.element.title},
 * the DOM title attribute, NOT {@code gtm.element.dataset.*} — that is what the
 * tag's {@code {{Title Attribute}}} variable reads. The tag also sends
 * {@code page_url}, which is what separates the same choice made in the cart
 * from the one made at checkout. No-op during SSR.
 *
 * @param option which method was picked — {@code "Home"}, {@code "Shop"},
 *        {@code "Express"} or {@code "Box Now"}
 */
export function dataLayerPushSelectDeliveryType(option: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Select_Delivery_Type',
    'gtm.element.title': `Delivery Option ${option}`,
  });
}

/**
 * Legacy {@code data-section} for the DAAS same-day delivery timeslot list
 * (deliveryMethods.js:2021, 2123). Legacy pushed the identical section from both
 * its popup and its inline slot strip, so the two read as one control.
 */
export const DAAS_TIMESLOT_SECTION = 'Time - Delivery Option Home Daas';

/**
 * Pushes the legacy GA4 timeslot event (deliveryMethods.js:2017-2022 and
 * 2120-2124). Fire it when the shopper picks a delivery timeslot.
 *
 * <p>The value carries BOTH the verb and the slot, because legacy's picker was a
 * toggle: clicking the chosen slot again deselected it and dropped the order
 * back off same-day delivery, which it reported as {@code "Reject <slot>"}. Our
 * modal only ever selects — the chosen slot is re-confirmed rather than cleared
 * — so {@code "Reject"} is currently unreachable. It stays in the signature
 * because it is part of the event's contract, and the tag's
 * {@code choice_value} is read as a verb-plus-slot string either way.
 *
 * <p>The tag reads {@code title} from {@code dataset.section} and
 * {@code choice_value} from {@code dataset.value}. No-op during SSR.
 *
 * @param action  {@code "Select"} or {@code "Reject"}
 * @param slot    the slot label exactly as shown, e.g. {@code "10:00-11:00"}
 * @param section which timeslot list this is, e.g. {@link DAAS_TIMESLOT_SECTION}
 */
export function dataLayerPushSelectTime(
  action: 'Select' | 'Reject',
  slot: string,
  section: string,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Select_Time',
    'gtm.element.dataset.value': `${action} ${slot}`,
    'gtm.element.dataset.section': section,
  });
}

/**
 * Pushes the legacy GA4 payment-method event (checkout-ui.js:105-134). Fire it
 * when the shopper PICKS a payment method, and only then.
 *
 * <p>Legacy hung this off the radio's {@code change} event, so it fired on a
 * real change and never on the default. Our method is also rewritten by
 * restriction effects — cash disabled by an express timeslot, IRIS switched off,
 * electronic-only orders forced to card — so callers fire from the click and
 * only when the method actually changed.
 *
 * <p>Keyed on {@code gtm.element.title} like its delivery twin. The
 * {@code " - Payment Method Form"} suffix is legacy's and is applied here so it
 * cannot drift between call sites. No-op during SSR.
 *
 * @param method which method was picked — {@code "Card"}, {@code "Iris"},
 *        {@code "Cash"}, {@code "Apple Pay"} or {@code "Google Pay"}
 */
export function dataLayerPushSelectPaymentType(method: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Select_Payment_Type',
    'gtm.element.title': `${method} - Payment Method Form`,
  });
}

/**
 * Pushes the legacy GA4 AADE Article 39 visibility event (checkout-ui.js:1554).
 * This is a VISIBILITY signal, not a click: legacy fired it while handling the
 * invoice radio, once the {@code .aade-article39-wrapper} existed in the DOM, so
 * that drop-off could be measured against how often the VAT-exemption form was
 * actually offered. The buttons inside the form stay on
 * {@link dataLayerPushClickButton}.
 *
 * <p>Only {@code gtm.element.title} is carried — legacy pushed no action or
 * section for this one. No-op during SSR.
 *
 * @param title the form's heading suffixed with its area, matching legacy's
 *        {@code <heading text> + " - Article39 Form"}
 */
export function dataLayerPushArticle39FormIsVisible(title: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Article39_Form_Is_Visible',
    'gtm.element.title': title,
  });
}

/**
 * Pushes the legacy GA4 AADE Article 39 discount event
 * (checkout-ui-maps-2.js:5464). This is the OUTCOME of the VAT exemption, not a
 * click: legacy fired it from the pricing refresh that follows a successful
 * verification, so the funnel reads "the form was offered"
 * ({@link dataLayerPushArticle39FormIsVisible}) → "someone submitted it"
 * ({@link dataLayerPushClickButton}) → "and this much VAT came off".
 *
 * <p>Legacy guarded on a non-zero amount and on the section not already being in
 * its success state, which together mean one report per discount actually
 * granted. Callers must hold that line: fire on the two moments the exemption is
 * newly applied — verification passing outright, and verification passing after
 * OTP — and never when a returning user's already-verified state is merely
 * restored on page load, or the same discount would be counted again on every
 * refresh.
 *
 * <p>The title is a fixed English label rather than anything from the page, so
 * the amount is the only thing that varies between reports — that is legacy's
 * string, kept verbatim. Pass the amount as a number, not a formatted string:
 * legacy went to the trouble of stripping the euro sign and swapping the Greek
 * decimal comma for a point precisely so the value would sum in a report.
 * No-op during SSR.
 *
 * @param amount the VAT discount granted, in euros
 */
export function dataLayerPushArticle39ApplyDiscount(amount: number): void {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  safePush(w.dataLayer, {
    event: 'Article39_Apply_Discount',
    'gtm.element.title': 'Discount applied successfully value',
    'gtm.element.dataset.value': amount,
  });
}

